import { useEffect, useMemo, useRef } from 'react';
import { ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

type CurtainsProps = { opening: boolean; reducedMotion: boolean; onVisible: () => void };
type CurtainController = { progress: { current: number }; grab: (side: -1 | 1, event: ThreeEvent<PointerEvent>) => void };

function makeCurtainGeometry() {
  const geometry = new THREE.PlaneGeometry(8.2, 26, 88, 108);
  const position = geometry.attributes.position;
  for (let index = 0; index < position.count; index += 1) {
    const x = position.getX(index);
    const y = position.getY(index);
    const fold = Math.sin((x + 4.1) * 4.1) * 0.28;
    const fineFold = Math.sin((x + 4.1) * 8.2 + y * 0.46) * 0.058;
    position.setZ(index, fold + fineFold + Math.cos(y * 1.25 + x * 0.28) * 0.04);
  }
  geometry.computeVertexNormals();
  geometry.translate(0, 0, 0.06);
  return geometry;
}

function useVelvetTexture() {
  const texture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext('2d')!;
    context.fillStyle = '#210611';
    context.fillRect(0, 0, 512, 512);
    for (let x = 0; x < 512; x += 2) {
      const value = 15 + Math.sin(x * 0.076) * 8 + Math.sin(x * 0.19) * 3;
      context.fillStyle = `rgb(${value + 30}, ${value}, ${value + 9})`;
      context.fillRect(x, 0, 2, 512);
    }
    context.globalAlpha = 0.12;
    for (let y = 0; y < 512; y += 6) {
      context.fillStyle = y % 12 ? '#25030e' : '#71304b';
      context.fillRect(0, y, 512, 1);
    }
    const result = new THREE.CanvasTexture(canvas);
    result.colorSpace = THREE.SRGBColorSpace;
    result.wrapS = THREE.RepeatWrapping;
    result.wrapT = THREE.RepeatWrapping;
    result.repeat.set(1.3, 2.3);
    result.anisotropy = 4;
    return result;
  }, []);
  useEffect(() => () => texture.dispose(), [texture]);
  return texture;
}

function CurtainHalf({ side, controller, reducedMotion, velvet }: { side: -1 | 1; controller: CurtainController; reducedMotion: boolean; velvet: THREE.Texture }) {
  const outer = useRef<THREE.Group>(null!);
  const fabric = useRef<THREE.Group>(null!);
  const geometry = useMemo(makeCurtainGeometry, []);
  const originalPositions = useMemo(() => new Float32Array((geometry.attributes.position.array as Float32Array)), [geometry]);
  const previousProgress = useRef(-1);
  const { size } = useThree();
  const mobile = size.width < 720;
  const closedX = side * 4.06;
  const openedX = side * (mobile ? 6.25 : 9.75);

  useEffect(() => () => geometry.dispose(), [geometry]);
  useFrame((state) => {
    const progress = controller.progress.current;
    const eased = progress * progress * (3 - 2 * progress);
    outer.current.position.x = THREE.MathUtils.lerp(closedX, openedX, eased);
    fabric.current.scale.x = THREE.MathUtils.lerp(1, mobile ? 0.47 : 0.54, eased);
    fabric.current.scale.z = THREE.MathUtils.lerp(1, 1.58, eased);
    fabric.current.position.x = side * eased * 0.42;
    fabric.current.rotation.z = side * eased * 0.075;
    fabric.current.rotation.y = reducedMotion ? 0 : Math.sin(state.clock.elapsedTime * 0.65 + side) * 0.026 * (1 - eased * 0.25);
    if (Math.abs(progress - previousProgress.current) > 0.002) {
      const position = geometry.attributes.position as THREE.BufferAttribute;
      for (let index = 0; index < position.count; index += 1) {
        const point = index * 3;
        const x = originalPositions[point];
        const y = originalPositions[point + 1];
        const gatheredFold = Math.sin((x + 4.1) * (8.2 + eased * 7.6) + y * 0.46) * eased * 0.17;
        position.setZ(index, originalPositions[point + 2] + gatheredFold);
      }
      position.needsUpdate = true;
      geometry.computeVertexNormals();
      previousProgress.current = progress;
    }
  });

  return <group ref={outer} position={[closedX, 0, 2.45]}>
    <group ref={fabric} onPointerDown={(event) => controller.grab(side, event)}>
      <mesh geometry={geometry} castShadow receiveShadow>
        <meshPhysicalMaterial map={velvet} bumpMap={velvet} bumpScale={0.055} color="#600d24" roughness={0.7} metalness={0.02} sheen={0.35} sheenColor="#c63e5b" side={THREE.DoubleSide} />
      </mesh>
      <mesh geometry={geometry} position={[0, 0, -0.13]} castShadow>
        <meshStandardMaterial map={velvet} color="#21030d" roughness={0.86} side={THREE.BackSide} />
      </mesh>
      <mesh position={[0, 12.95, 0.02]} castShadow><boxGeometry args={[8.24, 0.12, 0.22]} /><meshStandardMaterial color="#0b0910" metalness={0.15} roughness={0.65} /></mesh>
    </group>
  </group>;
}

function CurtainMainWash() {
  const light = useRef<THREE.SpotLight>(null!);
  const target = useRef<THREE.Object3D>(null!);
  useEffect(() => { light.current.target = target.current; }, []);
  return <group>
    <object3D ref={target} position={[0, -0.35, 0]} />
    <spotLight ref={light} color="#ffe3b4" position={[0, 4.7, 7.2]} intensity={900} angle={0.7} penumbra={0.92} distance={24} decay={1.12} />
  </group>;
}

export default function Curtains({ opening, reducedMotion, onVisible }: CurtainsProps) {
  const velvet = useVelvetTexture();
  const { gl } = useThree();
  const progress = useRef(0);
  const target = useRef(0);
  const manualControl = useRef(false);
  const drag = useRef({ active: false, side: -1 as -1 | 1, startX: 0, startProgress: 0 });

  const grab = (side: -1 | 1, event: ThreeEvent<PointerEvent>) => {
    if (!opening || (event.nativeEvent.pointerType === 'mouse' && event.nativeEvent.button !== 0)) return;
    event.stopPropagation();
    manualControl.current = true;
    drag.current.active = true;
    drag.current.side = side;
    drag.current.startX = event.nativeEvent.clientX;
    drag.current.startProgress = progress.current;
    gl.domElement.setPointerCapture?.(event.nativeEvent.pointerId);
    gl.domElement.style.cursor = 'grabbing';
  };

  useEffect(() => {
    const frame = window.requestAnimationFrame(onVisible);
    return () => window.cancelAnimationFrame(frame);
  }, [onVisible]);

  useEffect(() => {
    const canvas = gl.domElement;
    const move = (event: PointerEvent) => {
      if (!drag.current.active) return;
      const delta = (event.clientX - drag.current.startX) / Math.max(canvas.clientWidth, 1);
      target.current = THREE.MathUtils.clamp(drag.current.startProgress + drag.current.side * delta * 2.15, 0, 1);
    };
    const release = (event: PointerEvent) => {
      if (!drag.current.active) return;
      target.current = target.current >= 0.5 ? 1 : 0;
      drag.current.active = false;
      canvas.releasePointerCapture?.(event.pointerId);
      canvas.style.cursor = 'grab';
    };
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', release);
    canvas.addEventListener('pointercancel', release);
    return () => {
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerup', release);
      canvas.removeEventListener('pointercancel', release);
    };
  }, [gl]);

  useFrame((_state, delta) => {
    if (!opening) {
      manualControl.current = false;
      target.current = 0;
    } else if (!manualControl.current) {
      target.current = 1;
    }
    progress.current = THREE.MathUtils.damp(progress.current, target.current, reducedMotion ? 11 : 2.7, delta);
  });

  const controller: CurtainController = { progress, grab };
  return <group>
    <ambientLight intensity={0.1} />
    <pointLight color="#f22c54" position={[-3.6, 2.2, 3.6]} intensity={22} distance={10} />
    <pointLight color="#d81e43" position={[3.6, 2.1, 3.6]} intensity={18} distance={10} />
    <CurtainHalf side={-1} controller={controller} reducedMotion={reducedMotion} velvet={velvet} />
    <CurtainHalf side={1} controller={controller} reducedMotion={reducedMotion} velvet={velvet} />
    <CurtainMainWash />
  </group>;
}
